import { useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

interface TermsOfUseProps {
  open: boolean;
  onClose: () => void;
  onScrollToEnd?: () => void;
}

export function TermsOfUse({ open, onClose, onScrollToEnd }: TermsOfUseProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    if (contentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = contentRef.current;
      if (scrollTop + clientHeight >= scrollHeight - 10) {
        if (typeof onScrollToEnd === 'function') {
          onScrollToEnd();
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Terms of Use</DialogTitle>
        </DialogHeader>

        <div className="h-[60vh] overflow-y-auto pr-4" ref={contentRef} onScroll={handleScroll}>
          <div className="space-y-4 text-sm">
            <p className="text-gray-600">Effective: January 31, 2024</p>
            <p className="text-gray-700">Thank you for using Patent Loop!</p>

            <div>
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="flex items-center gap-2 font-semibold text-gray-900 hover:text-gray-700"
              >
                Registration and Access
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {isExpanded && (
                <div className="mt-2 space-y-2 text-gray-700">
                  <p className="font-medium">Minimum age:</p>
                  <p>You must be at least 18 years old or the minimum age required in your country to consent to use the Services.</p>

                  <p className="font-medium mt-3">Registration:</p>
                  <p>You must provide accurate and complete information to register for an account to use our Services. You may not share your account credentials or make your account available to anyone else and are responsible for all activities that occur under your account. If you create an account or use the Services on behalf of another person or entity, you must have the authority to accept these Terms on their behalf.</p>
                </div>
              )}
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Using Our Services</h3>
              <ul className="list-disc list-inside space-y-1 ml-4 text-gray-700">
                <li>Subject to your compliance with these Terms, you may access and use our Services.</li>
                <li>You must comply with all applicable laws as well as our policies and guidelines.</li>
                <li>You may not use our Services for any illegal, harmful, or abusive activity.</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Content</h3>
              <p className="text-gray-700">
                You may provide input to the Services ("Input"), and receive output from the Services based on the Input ("Output"). Input and Output are collectively "Content." You are responsible for Content, including ensuring that it does not violate any applicable law or these Terms. You represent and warrant that you have all rights, licenses, and permissions needed to provide Input to our Services.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Ownership of Content</h3>
              <p className="text-gray-700">
                As between you and Patent Loop, and to the extent permitted by applicable law, you retain your ownership rights in Input and own the Output. We hereby assign to you all our right, title, and interest, if any, in and to Output.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">User Responsibility</h3>
              <p className="text-gray-700">
                You are responsible for maintaining the confidentiality of your data. Any use of the platform that results in mistaken distribution or other unintended outcomes is at your own risk. We are always available for help, support, and questions regarding the use of our Services.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Disclaimer of Warranties</h3>
              <p className="text-gray-700">
                Our services are provided "as is." Except to the extent prohibited by law, we and our affiliates and licensors make no warranties (express, implied, statutory, or otherwise) with respect to the services, and disclaim all warranties including, but not limited to, warranties of merchantability, fitness for a particular purpose, satisfactory quality, non-infringement, and quiet enjoyment, and any warranties arising out of any course of dealing or trade usage. We do not warrant that the services will be uninterrupted, accurate, or error-free, or that any content will be secure or not lost or altered.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">No Liability</h3>
              <p className="text-gray-700">
                Neither we nor any of our affiliates or licensors will be liable for any indirect, incidental, special, consequential, or exemplary damages, including damages for loss of profits, goodwill, use, or data or other losses, even if we have been advised of the possibility of such damages. Our aggregate liability under these terms will not exceed the greater of the amount you paid for the service that gave rise to the claim during the 12 months before the liability arose or one hundred dollars ($100). The limitations in this section apply only to the maximum extent permitted by applicable law.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Legal Advice Disclaimer</h3>
              <p className="text-gray-700">
                This application does not provide legal advice. The information is provided for general informational and educational purposes only and is not a substitute for professional legal advice. Accordingly, before taking any actions based upon such information, we encourage you to consult with the appropriate legal professionals or licensing authorities.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">AI Hallucinations Disclaimer</h3>
              <p className="text-gray-700">
                Generative text AI, like the models used in Patent Loop, may sometimes produce incorrect or nonsensical information, known as AI hallucinations. It is important to critically evaluate the outputs and verify them with trusted sources. For more information on AI hallucinations and how to handle them, please refer to this <a href="https://www.stellarcapacity.com/post/understanding-the-phenomenon-of-ai-hallucinations" className="text-blue-600 underline" target="_blank" rel="noopener noreferrer">guidance on understanding AI hallucinations</a>.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Contact Information</h3>
              <p className="text-gray-700">
                ForGen AI, LLC<br />
                Birmingham, Michigan 48009<br />
                Email: support@forgen.ai
              </p>
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
